import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { v4 } from 'uuid';

import { InfoIcon } from '../InfoIcon/InfoIcon.component';
import { Tooltip } from '../Tooltip';
import { TooltipPlacementType } from '../Tooltip/Tooltip.component';
import './index.css';

export const Switcher: React.FC<
  React.PropsWithChildren<{
    className?: string;
    name: string;
    checked?: boolean | undefined;
    disabled?: boolean;
    label?: React.ReactNode | string;
    dataAttribute?: string;
    tooltip?: string;
    tooltipPlacement?: TooltipPlacementType;
    disabledTooltip?: string;
    onChange?: (checked: boolean) => void;
  }>
> = React.memo(
  ({
    name,
    checked = false,
    label,
    disabled,
    tooltip,
    dataAttribute = '',
    onChange,
    className = '',
    tooltipPlacement = 'top',
  }) => {
    const form = useFormContext();

    const tooltipNode = useMemo(() => {
      if (!tooltip) return null;

      return (
        <Tooltip enabled placement={tooltipPlacement} overlay={tooltip}>
          <span className='switcher__label-tooltip'>
            <InfoIcon type='coin' />
          </span>
        </Tooltip>
      );
    }, [tooltip, tooltipPlacement]);

    const id = useMemo(() => v4(), []);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(event.target.checked);
      },
      [onChange],
    );

    const inputConfig = useMemo(() => {
      if (form) return { ...form.register(name) };

      return { name, onChange: handleChange, checked };
    }, [checked, form, handleChange, name]);

    const wrapperClassName = useMemo(() => classNames('switcher', { [`${className}`]: className }), [className]);

    return (
      <div data-test={dataAttribute} className={wrapperClassName}>
        {!!label && (
          <div className='switcher__label'>
            {label}
            {tooltipNode}
          </div>
        )}
        <label className='toggler' htmlFor={id}>
          <input
            type='checkbox'
            {...inputConfig}
            id={id}
            className='toggler__input'
            disabled={disabled}
            data-test={dataAttribute}
          />
          <span className='toggler__slider' />
        </label>
      </div>
    );
  },
);
